import axios from "axios";
import { ENV } from "../config/env";
import {
  clearAuthSession,
  getStoredRefreshToken,
  getStoredToken,
  saveTokens,
} from "./authSession";
type AxiosRequestConfigAny = any;
const axiosLib = axios as any;

const baseURL = ENV.API_BASE_URL;

const systemUser = ENV.SYSTEM_USER || "";
const systemPassword = ENV.SYSTEM_PASSWORD || "";

interface TokenResponse {
  accessToken?: string;
  refreshToken?: string;
  token?: string;
}

export function clearAuth(): void {
  clearAuthSession();
}

const api = axiosLib.create({ baseURL });

let devSwaggerTokenLogged = false;

function isPublicAuthEndpoint(url: string): boolean {
  return (
    url.includes("/api/login/loginInit") ||
    url.includes("/api/login/login") ||
    url.includes("/api/login/refresh") ||
    url.includes("/api/auth/loginInit") ||
    url.includes("/api/auth/login") ||
    url.includes("/api/auth/refresh")
  );
}

// --------- REQUEST interceptor: coloca Bearer ---------
api.interceptors.request.use(
  (config: AxiosRequestConfigAny) => {
    const token = getStoredToken();
    const url = String(config?.url || "");

    if (token && !isPublicAuthEndpoint(url)) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;

      if (process.env.NODE_ENV === "development" && !devSwaggerTokenLogged) {
        devSwaggerTokenLogged = true;
        // Facilita copiar no DevTools: use window.__MERITO_TOKEN
        (window as any).__MERITO_TOKEN = token;
      }
    }

    // FormData: garantir que o browser defina multipart com boundary
    // (Content-Type manual sem boundary → 415 no ASP.NET).
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      const headers = config.headers;
      if (headers) {
        if (typeof headers.delete === "function") {
          headers.delete("Content-Type");
          headers.delete("content-type");
        } else if (typeof headers.set === "function") {
          headers.set("Content-Type", false);
        } else {
          delete headers["Content-Type"];
          delete headers["content-type"];
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// --------- RESPONSE interceptor: refresh automático ---------
let isRefreshing = false;
let refreshPromise: Promise<{ data: TokenResponse }> | null = null;

api.interceptors.response.use(
  (response: any) => response,
  async (error: {
    config?: AxiosRequestConfigAny;
    response?: { status?: number };
  }) => {
    const originalRequest = error.config as AxiosRequestConfigAny & {
      _retry?: boolean;
    };

    const status = error.response?.status;
    const url = String(originalRequest?.url || "");

    if (status !== 401) return Promise.reject(error);
    if (isPublicAuthEndpoint(url)) return Promise.reject(error);

    if (originalRequest._retry) return Promise.reject(error);
    originalRequest._retry = true;

    const storedRefresh = getStoredRefreshToken();
    if (!storedRefresh) {
      clearAuth();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    try {
      if (!isRefreshing) {
        isRefreshing = true;

        refreshPromise = axiosLib.post(
          `${baseURL}/api/auth/refresh`,
          { refreshToken: storedRefresh },
          { headers: { "Content-Type": "application/json" } },
        );
      }

      const refreshResponse = await refreshPromise;

      const {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        token,
      } = (refreshResponse?.data as TokenResponse) || {};

      const finalAccess = newAccessToken || token;
      if (!finalAccess) throw new Error("Refresh não retornou accessToken.");

      saveTokens({
        accessToken: finalAccess,
        refreshToken: newRefreshToken,
      });

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${finalAccess}`;

      isRefreshing = false;
      refreshPromise = null;

      return api(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      refreshPromise = null;
      clearAuth();
      window.location.href = "/login";
      return Promise.reject(refreshError);
    }
  },
);

export function getSystemCredentials(): {
  nomeUsuarioSistema: string;
  senhaUsuarioSistema: string;
} {
  return {
    nomeUsuarioSistema: systemUser,
    senhaUsuarioSistema: systemPassword,
  };
}

export function extractAxiosErrorMessage(error: unknown): string | null {
  const e = error as {
    response?: { data?: unknown; status?: number };
    message?: string;
  };
  const status = e?.response?.status;
  const data = e?.response?.data;

  if (data && typeof data === "object" && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    const msgs = o.errorMessages;
    if (Array.isArray(msgs) && msgs.length > 0) {
      const s = msgs
        .filter((x): x is string => typeof x === "string" && Boolean(x.trim()))
        .join(" ");
      if (s) return s;
    }
    for (const key of ["title", "detail", "message"]) {
      const v = o[key];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
  }
  if (typeof data === "string" && data.trim()) return data.trim();

  if (status === 403) {
    return "Acesso negado (403). Verifique permissão ou se a solicitação permite esta alteração no estado atual.";
  }
  if (status === 401) {
    return "Acesso não autorizado (401). Faça login novamente.";
  }
  return null;
}

export default api;
